import { GetOrganizationResponse, GetStyleResponse } from "./../App/Organization/sagas";
import { AxiosResponse } from "axios";
import { zip } from "ramda";
import { all, call, put } from "redux-saga/effects";
import { ActionCreator, IAction } from "reduxsauce";

import { loadExportedSCORMPackagesAmount } from "../actions/load-amounts";
import { IOrgDisplayData } from "../api/ResolveApi/resolve-api.types";
import { IObjectsAmount } from "../App/actions";
import { dependencies } from "../dependencies";
import {
  loginPostErrorResponseToLoginUIData,
  loginPostSuccessResponseToLoginUIData,
  loginUIDataToLoginFormBody,
} from "../parsers/login";
import {
  hasErrorRecords,
  IOrganizationUIData,
  resolveAccountPostSuccessResponseValueToLoginUIData,
} from "../parsers/resolve";
import { SagaAction } from "../sagas/types";
import { getSkinUrls } from "../utils";
import { Creators } from "./common";
import { loadAdministrableOrdIds } from "./organizations";
import { SagaFunction } from "./types";
import { _getOrganizationById, _getStyleById } from "../App/Organization/sagas";

export interface IFetchLocalAuthenticationFurtherActions {
  onSuccess: ActionCreator;
}

export interface ILogInFurtherActions {
  onFailure: ActionCreator;
  onSuccess: ActionCreator;
}

export function* fetchLocalCredentials() {
  const { window, saltcomplianceLocalStorageKeys } = dependencies;
  const accountId = yield call(
    [window.localStorage, window.localStorage.getItem],
    saltcomplianceLocalStorageKeys.accountId
  );
  const organizationId = yield call(
    [window.localStorage, window.localStorage.getItem],
    saltcomplianceLocalStorageKeys.organizationId
  );
  const root = yield call([window.localStorage, window.localStorage.getItem], saltcomplianceLocalStorageKeys.root);
  const version = yield call(
    [window.localStorage, window.localStorage.getItem],
    saltcomplianceLocalStorageKeys.version
  );
  const courseAmount = yield call(
    [window.localStorage, window.localStorage.getItem],
    saltcomplianceLocalStorageKeys.courseAmount
  );
  const classAmount = yield call(
    [window.localStorage, window.localStorage.getItem],
    saltcomplianceLocalStorageKeys.classAmount
  );
  const groupsAmount = yield call(
    [window.localStorage, window.localStorage.getItem],
    saltcomplianceLocalStorageKeys.groupsAmount
  );
  const usersAmount = yield call(
    [window.localStorage, window.localStorage.getItem],
    saltcomplianceLocalStorageKeys.usersAmount
  );
  const exportedScormPackagesAmount = yield call(
    [window.localStorage, window.localStorage.getItem],
    saltcomplianceLocalStorageKeys.exportedScormPackagesAmount
  );
  return [
    accountId,
    organizationId,
    root,
    version,
    courseAmount,
    classAmount,
    exportedScormPackagesAmount,
    groupsAmount,
    usersAmount,
  ];
}

export function* removeLocalStorageAuthKeys() {
  const { window, saltcomplianceLocalStorageKeys } = dependencies;
  yield call([window.localStorage, window.localStorage.removeItem], saltcomplianceLocalStorageKeys.accountId);
  yield call([window.localStorage, window.localStorage.removeItem], saltcomplianceLocalStorageKeys.organizationId);
  yield call([window.localStorage, window.localStorage.removeItem], saltcomplianceLocalStorageKeys.root);
  yield call([window.localStorage, window.localStorage.removeItem], saltcomplianceLocalStorageKeys.version);
  yield call([window.localStorage, window.localStorage.removeItem], saltcomplianceLocalStorageKeys.courseAmount);
  yield call([window.localStorage, window.localStorage.removeItem], saltcomplianceLocalStorageKeys.classAmount);
  yield call([window.localStorage, window.localStorage.removeItem], saltcomplianceLocalStorageKeys.groupsAmount);
  yield call([window.localStorage, window.localStorage.removeItem], saltcomplianceLocalStorageKeys.usersAmount);
  yield call(
    [window.localStorage, window.localStorage.removeItem],
    saltcomplianceLocalStorageKeys.exportedScormPackagesAmount
  );
  yield call([window.localStorage, window.localStorage.removeItem], saltcomplianceLocalStorageKeys.fetchTimestamp);
}

export function* resolveCredentials(accountId: string, organizationId: string) {
  const { api } = dependencies;
  return yield all({
    accountsResult: call(api.resolve.account.post, accountId, [accountId]),
    organizationsResult: call(api.resolve.organizationDisplay.post, accountId, [organizationId]),
  });
}

export const fetchLocalAuthentication: (
  furtherActions: IFetchLocalAuthenticationFurtherActions
) => SagaFunction = furtherActions =>
  function* _fetchLocalAuthentication(action: IAction): SagaAction {
    const { saltcomplianceApiRoot, saltcomplianceVersion } = dependencies;
    const [
      accountId,
      organizationId,
      root,
      version,
      courseAmount,
      classAmount,
      exportedScormPackagesAmount,
      groupsAmount,
      usersAmount,
    ] = yield call(fetchLocalCredentials);
    const objectsAmount: IObjectsAmount = {
      classAmount,
      courseAmount,
      exportedScormPackagesAmount,
      groupsAmount,
      usersAmount,
    };
    if (accountId === null || organizationId === null || version === null || root === null) {
      yield call(removeLocalStorageAuthKeys);
      yield put(
        furtherActions.onSuccess(
          {
            canAccessDashboard: false,
            isAuthenticated: false,
            isKnown: true,
          },
          null,
          null,
          saltcomplianceApiRoot,
          saltcomplianceVersion
        )
      );
    } else {
      const { accountsResult, organizationsResult } = yield call(resolveCredentials, accountId, organizationId);
      const [accountsError, accountsResponse] = accountsResult;
      const [organizationsError, organizationsResponse] = organizationsResult;
      yield put(loadAdministrableOrdIds(accountId));
      if (accountsError || organizationsError || version !== saltcomplianceVersion || root !== saltcomplianceApiRoot) {
        yield call(removeLocalStorageAuthKeys);
        yield put(
          furtherActions.onSuccess(
            {
              canAccessDashboard: false,
              isAuthenticated: false,
              isKnown: true,
            },
            null,
            null,
            objectsAmount,
            saltcomplianceApiRoot,
            saltcomplianceVersion
          )
        );
      } else {
        if (hasErrorRecords(accountsResponse.data)) {
          yield put(
            furtherActions.onSuccess(
              {
                canAccessDashboard: false,
                isAuthenticated: false,
                isKnown: true,
              },
              null,
              null,
              objectsAmount,
              saltcomplianceApiRoot,
              saltcomplianceVersion
            )
          );
        } else {
          const [account] = accountsResponse.data.map(resolveAccountPostSuccessResponseValueToLoginUIData);
          if (!account.canAccessDashboard) {
            yield put(
              furtherActions.onSuccess(
                {
                  canAccessDashboard: account.canAccessDashboard,
                  isAuthenticated: true,
                  isKnown: true,
                },
                { ...account, accountId },
                null,
                objectsAmount,
                saltcomplianceApiRoot,
                saltcomplianceVersion
              )
            );
          } else {
            if (hasErrorRecords(organizationsResponse.data)) {
              yield put(
                furtherActions.onSuccess(
                  {
                    canAccessDashboard: account.canAccessDashboard,
                    isAuthenticated: true,
                    isKnown: true,
                  },
                  { ...account, accountId },
                  null,
                  objectsAmount,
                  saltcomplianceApiRoot,
                  saltcomplianceVersion
                )
              );
            } else {
              const [organization] = zip<string, IOrgDisplayData>([organizationId], organizationsResponse.data).map(
                ([uuid, datum]) => ({
                  defaultLocale: null,
                  displayName: datum.value.displayName,
                  organizationId: uuid,
                  ownerOrganizationId: datum.value.owner,
                  releaseLine: "",
                  style: null,
                })
              );
              const [, organizationResp]: GetOrganizationResponse = yield call(
                _getOrganizationById,
                organizationId,
                accountId
              );
              let style = null;
              if (organizationResp?.style) {
                const resp: GetStyleResponse = yield call(_getStyleById, organizationResp.style, accountId);
                style = resp[1];
              }
              yield put(
                furtherActions.onSuccess(
                  {
                    canAccessDashboard: account.canAccessDashboard,
                    isAuthenticated: true,
                    isKnown: true,
                  },
                  { ...account, accountId },
                  { ...organization, style },
                  objectsAmount,
                  saltcomplianceApiRoot,
                  saltcomplianceVersion
                )
              );
            }
          }
        }
      }
    }
  };

export const handleFetchLocalAuthenticationSuccess: (furtherActions: {}) => SagaFunction = furtherActions =>
  function* _handleFetchLocalAuthenticationSuccess(action: IAction) {
    const { window, saltcomplianceUserDashboardUrl } = dependencies;
    if (action.authenticationState.isAuthenticated && !action.authenticationState.canAccessDashboard) {
      yield call([window.location, window.location.replace], saltcomplianceUserDashboardUrl);
    }
  };

export const login: (furtherActions: ILogInFurtherActions) => SagaFunction = furtherActions =>
  function* _login(action: IAction): SagaAction {
    const { api } = dependencies;
    const [loginError, loginResponse] = yield call(api.login.post, loginUIDataToLoginFormBody(action.data));
    if (loginError) {
      yield put(
        furtherActions.onFailure(
          loginPostErrorResponseToLoginUIData(loginError.data, {
            response: loginError.response,
          }),
          null
        )
      );
    } else {
      const user = loginPostSuccessResponseToLoginUIData(loginResponse.data);
      const [organizationError, organizationResponse]: [string, AxiosResponse<[IOrgDisplayData]>] = yield call(
        api.resolve.organizationDisplay.post,
        user.accountId,
        [user.ownerOrganizationId]
      );
      if (organizationError) {
        yield put(furtherActions.onSuccess(user, null));
      } else if (hasErrorRecords(organizationResponse.data)) {
        yield put(furtherActions.onSuccess(user, null));
      } else {
        const [organization] = organizationResponse.data.map<IOrganizationUIData>(data => ({
          defaultLocale: null,
          displayName: data.value.displayName,
          ownerOrganizationId: data.value.owner,
          releaseLine: "",
        }));
        const [, organizationResp]: GetOrganizationResponse = yield call(
          _getOrganizationById,
          user.ownerOrganizationId,
          user.accountId
        );
        let style = null;
        if (organizationResp?.style) {
          const resp: GetStyleResponse = yield call(_getStyleById, organizationResp.style, user.accountId);
          style = resp[1];
        }
        yield put(
          furtherActions.onSuccess(user, {
            ...organization,
            organizationId: user.ownerOrganizationId,
            style,
          })
        );
        yield put(Creators.loadClassAmount());
        yield put(Creators.loadCourseAmount());
        yield put(Creators.loadGroupsAmount());
        yield put(Creators.loadUsersAmount());
        yield put(loadExportedSCORMPackagesAmount());
        yield put(loadAdministrableOrdIds(user.accountId));
      }
    }
  };

export const handleLoginSuccess: (furtherActions: {}) => SagaFunction = furtherActions =>
  function* _handleLoginSuccess(action: IAction) {
    const {
      window,
      saltcomplianceUserDashboardUrl,
      saltcomplianceLocalStorageKeys,
      saltcomplianceVersion,
      saltcomplianceApiRoot,
    } = dependencies;
    yield call(
      [window.localStorage, window.localStorage.setItem],
      saltcomplianceLocalStorageKeys.organizationId,
      action.user.ownerOrganizationId
    );
    yield call(
      [window.localStorage, window.localStorage.setItem],
      saltcomplianceLocalStorageKeys.accountId,
      action.user.accountId
    );
    yield call(
      [window.localStorage, window.localStorage.setItem],
      saltcomplianceLocalStorageKeys.root,
      saltcomplianceApiRoot
    );
    yield call(
      [window.localStorage, window.localStorage.setItem],
      saltcomplianceLocalStorageKeys.version,
      saltcomplianceVersion
    );
    yield call(
      [window.localStorage, window.localStorage.setItem],
      saltcomplianceLocalStorageKeys.fetchTimestamp,
      `${Date.now()}`
    );
    if (!action.user.isAdmin) {
      yield call([window.location, window.location.replace], saltcomplianceUserDashboardUrl);
    } else {
      yield call(updateStyles, saltcomplianceApiRoot, action.user.ownerOrganizationId, saltcomplianceVersion);
    }
  };

export async function updateStyles(root: string, organizationId: string, version: string) {
  const { window } = dependencies;
  const { skin } = getSkinUrls(root, organizationId, version);

  // FIXME: This has to be made one the loading part has been completed. This
  // will be addressed on the next PR
  // await window.document.getElementById(logo.id).setAttribute("src", logo.url);
  const element = window.document.getElementById(skin.id);
  if (element) {
    element.setAttribute("href", skin.url);
  }
}

export interface ILogoutFurtherActions {
  onSuccess: ActionCreator;
}

export const logout: (furtherActons: ILogoutFurtherActions) => SagaFunction = furtherActons =>
  function* _logout(action: IAction): SagaAction {
    const { api } = dependencies;
    const [logoutError] = yield call(api.logout.delete);
    if (!logoutError) {
      yield call(removeLocalStorageAuthKeys);
      yield put(furtherActons.onSuccess());
    }
  };
